import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useOutletContext } from 'react-router-dom';
import {
    Box,
    TextField,
    Button,
    Typography,
    Paper,
    MenuItem,
    Select,
    FormControl,
    Alert,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    Divider
} from '@mui/material';
import { toast } from 'react-toastify';
import axios from 'axios';

const api = axios.create({ withCredentials: true });

function FeatureRequest(props) {
    const { current_broker, current_user } = useOutletContext();

    const { getAccessTokenSilently } = useAuth0();
    const [formData, setFormData] = useState({
        feature_description: '',
        importance_reason: '',
        how_it_works: '',
        priority: 3
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [brokerName, setBrokerName] = useState('Loading...');

    // Fetch broker name when component mounts
    React.useEffect(() => {
        const fetchBrokerName = async () => {
            if (current_broker === 'all') {
                setBrokerName('All Brokers');
                return;
            }

            try {
                const token = await getAccessTokenSilently({
                    audience: process.env.REACT_APP_AUTH0AUDIENCE,
                    scope: process.env.REACT_APP_AUTH0SCOPE
                });

                // Query the database to get client_broker_name for this buyer_broker_id
                const response = await api.get(
                    `${process.env.REACT_APP_BASEURL}/api/feature-request/broker-name/${current_broker}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                setBrokerName(response.data.client_broker_name || 'Unknown Broker');
            } catch (error) {
                console.error('Error fetching broker name:', error);
                setBrokerName('Unknown Broker');
            }
        };

        fetchBrokerName();
    }, [current_broker, getAccessTokenSilently]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate form
        if (!formData.feature_description.trim() || 
            !formData.importance_reason.trim() || 
            !formData.how_it_works.trim()) {
            toast.error('Please fill in all fields');
            return;
        }

        // Ensure broker name is loaded
        if (brokerName === 'Loading...') {
            toast.error('Please wait for broker information to load');
            return;
        }

        setIsSubmitting(true);

        try {
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0AUDIENCE,
                scope: process.env.REACT_APP_AUTH0SCOPE
            });

            const submissionData = {
                user_email: current_user.email,
                user_name: `${current_user.fname} ${current_user.lname}`,
                vendor: brokerName,
                ...formData
            };

            await api.post(
                `${process.env.REACT_APP_BASEURL}/api/feature-request/submit`,
                submissionData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success('Feature request submitted successfully!');
            
            // Reset form
            setFormData({
                feature_description: '',
                importance_reason: '',
                how_it_works: '',
                priority: 3
            });

            // Close the dialog
            if (props.onClose) {
                props.onClose();
            }

        } catch (error) {
            console.error('Error submitting feature request:', error);
            toast.error('Failed to submit feature request. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 800, margin: '0 auto' }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h5" component="h1" gutterBottom color="primary">
                    Feature Request Form
                </Typography>
                
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    We value your feedback! Please share any features or improvements you'd like to see.
                </Typography>

                <Alert severity="info" sx={{ mb: 3 }}>
                    <strong>Submitting as:</strong> {current_user.fname} {current_user.lname} ({current_user.email})
                    <br />
                    <strong>Vendor:</strong> {brokerName}
                </Alert>

                <form onSubmit={handleSubmit}>
                    <List component={Paper} variant="outlined">
                        <ListItem>
                            <ListItemText
                                primary={
                                    <Typography variant="h6" color="primary" gutterBottom>
                                        1. What is the feature you're requesting?
                                    </Typography>
                                }
                                secondary={
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        variant="outlined"
                                        placeholder="Describe the feature or functionality you'd like added..."
                                        value={formData.feature_description}
                                        onChange={(e) => handleInputChange('feature_description', e.target.value)}
                                        required
                                        disabled={isSubmitting}
                                        sx={{ mt: 1 }}
                                    />
                                }
                            />
                        </ListItem>
                        
                        <Divider />
                        
                        <ListItem>
                            <ListItemText
                                primary={
                                    <Typography variant="h6" color="primary" gutterBottom>
                                        2. Why is this feature important?
                                    </Typography>
                                }
                                secondary={
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        variant="outlined"
                                        placeholder="What problem does it solve or what benefit does it provide?"
                                        value={formData.importance_reason}
                                        onChange={(e) => handleInputChange('importance_reason', e.target.value)}
                                        required
                                        disabled={isSubmitting}
                                        sx={{ mt: 1 }}
                                    />
                                }
                            />
                        </ListItem>
                        
                        <Divider />
                        
                        <ListItem>
                            <ListItemText
                                primary={
                                    <Typography variant="h6" color="primary" gutterBottom>
                                        3. How should the feature work?
                                    </Typography>
                                }
                                secondary={
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        variant="outlined"
                                        placeholder="Briefly explain how you imagine it functioning or being used..."
                                        value={formData.how_it_works}
                                        onChange={(e) => handleInputChange('how_it_works', e.target.value)}
                                        required
                                        disabled={isSubmitting}
                                        sx={{ mt: 1 }}
                                    />
                                }
                            />
                        </ListItem>
                        
                        <Divider />
                        
                        <ListItem>
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Typography variant="h6" color="primary" sx={{ minWidth: '200px' }}>
                                            4. What is the Priority?
                                        </Typography>
                                        <FormControl sx={{ minWidth: 200 }} required variant="outlined">
                                            <Select
                                                value={formData.priority}
                                                onChange={(e) => handleInputChange('priority', e.target.value)}
                                                disabled={isSubmitting}
                                                displayEmpty
                                                size="small"
                                            >
                                                <MenuItem value={1}>1 - Low</MenuItem>
                                                <MenuItem value={2}>2 - Below Average</MenuItem>
                                                <MenuItem value={3}>3 - Average</MenuItem>
                                                <MenuItem value={4}>4 - High</MenuItem>
                                                <MenuItem value={5}>5 - Critical</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                }
                            />
                        </ListItem>
                    </List>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={isSubmitting || brokerName === 'Loading...'}
                            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit Feature Request'}
                        </Button>
                    </Box>
                </form>
            </Paper>
        </Box>
    );
}

export default FeatureRequest;
